// STIM-001's signed-vector operation, suitable for the app's live vertex layer.
// Inputs must come from the matched/verified bundle; this module performs no I/O.

export function createFieldSampler(header, fields) {
  const basis = header.basis, shape = header.arrays?.e_field?.shape;
  const V = header.arrays?.vertices?.shape?.[0];
  const fail = message => { throw new Error(`TMS basis: ${message}`); };
  if (header.format !== 'ba-stim-sim-v0' || basis?.algorithm !== 'bilinear-vector' ||
      !Array.isArray(shape) || shape.length !== 3 || shape[1] !== V || shape[2] !== 3 ||
      !Number.isInteger(V) || V < 1 || header.arrays.e_field.units !== 'V/m') fail('invalid layout or units');
  if (!(fields instanceof Float32Array) || fields.length !== shape[0] * V * 3) fail('wrong vector array length');
  if (!fields.every(Number.isFinite)) fail('nonfinite source field');
  const offsets = basis.offsets_mm, indices = basis.pose_indices;
  if (!Array.isArray(offsets) || !Array.isArray(indices) || offsets.length !== indices.length ||
      offsets.some(p => p.length !== 2 || !p.every(Number.isFinite))) fail('invalid grid');
  const xs = [...new Set(offsets.map(p => p[0]))].sort((a,b) => a-b);
  const ys = [...new Set(offsets.map(p => p[1]))].sort((a,b) => a-b);
  const lookup = new Map(offsets.map((p,i) => [p.join(','), indices[i]]));
  if (xs.length < 2 || ys.length < 2 || lookup.size !== offsets.length ||
      offsets.length !== xs.length * ys.length || new Set(indices).size !== indices.length) fail('incomplete grid');
  for (let i=0; i<indices.length; i++) {
    const index = indices[i], pose = header.poses?.find(p => p.index === index);
    if (!Number.isInteger(index) || index < 0 || index >= shape[0] || pose?.role !== 'train' ||
        pose.offset_mm?.[0] !== offsets[i][0] || pose.offset_mm?.[1] !== offsets[i][1]) fail('pose/grid mismatch');
  }
  const slew = header.poses.find(p=>p.index===indices[0]).didt_A_per_s;
  if (!Number.isFinite(slew) || slew<=0 || indices.some(i=>header.poses.find(p=>p.index===i).didt_A_per_s!==slew)) fail('basis must share one reference slew rate');
  const bounds = [[xs[0],xs.at(-1)], [ys[0],ys.at(-1)]];
  if (JSON.stringify(bounds) !== JSON.stringify(basis.valid_rectangle_mm) || basis.outside !== 'reject') fail('domain mismatch');
  const coeff = new Float32Array(4), starts = new Uint32Array(4);
  function select(x,y) {
    if (!Number.isFinite(x) || !Number.isFinite(y) || x<xs[0] || x>xs.at(-1) || y<ys[0] || y>ys.at(-1))
      throw new RangeError('Coil offset is outside the validated TMS patch');
    let a=0,b=0;
    while (a<xs.length-2 && x>=xs[a+1]) a++;
    while (b<ys.length-2 && y>=ys[b+1]) b++;
    const u=(x-xs[a])/(xs[a+1]-xs[a]), v=(y-ys[b])/(ys[b+1]-ys[b]);
    coeff.set([(1-u)*(1-v),u*(1-v),(1-u)*v,u*v]);
    starts[0]=lookup.get(`${xs[a]},${ys[b]}`)*V*3;
    starts[1]=lookup.get(`${xs[a+1]},${ys[b]}`)*V*3;
    starts[2]=lookup.get(`${xs[a]},${ys[b+1]}`)*V*3;
    starts[3]=lookup.get(`${xs[a+1]},${ys[b+1]}`)*V*3;
  }
  function outputCheck(magnitude, vector, scale) {
    if (!(magnitude instanceof Float32Array) || magnitude.length!==V ||
        (vector!==null && (!(vector instanceof Float32Array) || vector.length!==V*3)) || !Number.isFinite(scale))
      fail('invalid output buffer or intensity');
    if (magnitude.buffer===fields.buffer || vector?.buffer===fields.buffer ||
        vector?.buffer===magnitude.buffer) fail('output buffers must not alias source or each other');
  }
  return {
    vertexCount: V, bounds,
    // Caller-owned buffers avoid a full-cortex allocation on every pointer move.
    // Compute signed components first. Interpolating |E| would fail cancellation.
    sample(x,y,magnitude,scale=1,vector=null) {
      outputCheck(magnitude,vector,scale); select(x,y);
      const [a,b,c,d]=starts, [wa,wb,wc,wd]=coeff;
      for (let i=0,j=0; i<V; i++,j+=3) {
        const ex=fields[a+j]*wa+fields[b+j]*wb+fields[c+j]*wc+fields[d+j]*wd;
        const ey=fields[a+j+1]*wa+fields[b+j+1]*wb+fields[c+j+1]*wc+fields[d+j+1]*wd;
        const ez=fields[a+j+2]*wa+fields[b+j+2]*wb+fields[c+j+2]*wc+fields[d+j+2]*wd;
        magnitude[i]=Math.abs(scale)*Math.sqrt(ex*ex+ey*ey+ez*ez);
        if (vector) { vector[j]=scale*ex; vector[j+1]=scale*ey; vector[j+2]=scale*ez; }
      }
      return magnitude;
    },
    // Registered trivial control, also used for separate copy+norm timing.
    nearest(x,y,magnitude,scale=1,vector=null) {
      outputCheck(magnitude,vector,scale); select(x,y);
      let best=0, distance=Infinity;
      offsets.forEach((p,i) => { const d=(p[0]-x)**2+(p[1]-y)**2; if(d<distance) { best=i; distance=d; } });
      const start=indices[best]*V*3;
      for(let i=0,j=0;i<V;i++,j+=3) {
        const ex=fields[start+j],ey=fields[start+j+1],ez=fields[start+j+2];
        magnitude[i]=Math.abs(scale)*Math.sqrt(ex*ex+ey*ey+ez*ez);
        if(vector) { vector[j]=scale*ex; vector[j+1]=scale*ey; vector[j+2]=scale*ez; }
      }
      return magnitude;
    },
  };
}

export function columnMajorPose(rows) {
  if (!Array.isArray(rows) || rows.length!==4 || rows.some(r=>!Array.isArray(r)||r.length!==4||!r.every(Number.isFinite)) ||
      rows[3].some((v,i)=>Math.abs(v-(i===3?1:0))>1e-10)) throw new Error('Expected a finite homogeneous pose');
  return Float32Array.from({length:16},(_,i)=>rows[i%4][Math.floor(i/4)]);
}
